import { Component } from '@angular/core';
import { Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { formatDate } from '@angular/common';
import { FitnessPlanService } from '../fitness-plan.service';
@Component({
  selector: 'app-basic-info',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './basic-info.component.html',
  styleUrl: './basic-info.component.css',
})
export class BasicInfoComponent {
  @Input() member: any;
  ht: any;
  wt: any = '---';
  imgSrc: any = 'assets/img/defaultUser.png';
  imgAssignedUnassigned: any = 'assets/img/unassigned.png';
  bmi: string = '---';
  hasExistingPlan: boolean = false;
  fitnessPlanName: string = '';

  constructor(private fitnessPlanService: FitnessPlanService) {}

  ngOnInit(): void {
    console.log('BasicInfoComponent initialized with member:', this.member);

    this.ht = this.member.height ? Math.trunc(this.member.height) : '---';
    if (this.member.weight_lb) {
      this.wt = Math.trunc(parseFloat(this.member.weight_lb)).toString() + ' lb';
    }
    if (this.member.profilePicture && this.member.profilePicture != '') {
      this.imgSrc = this.member.profilePicture;
    }

    // Check if member has existing fitness plan data from the API response
    this.hasExistingPlan = this.isMemberAssigned();

    if (this.hasExistingPlan) {
      this.imgAssignedUnassigned = 'assets/img/assigned.png';
      // Call API to get fitness plan name
      this.getFitnessPlanName();
    }

    // Calculate BMI if height and weight are available
    if (this.member.height && this.member.weight) {
      const heightInMeters = this.member.height / 100;
      const bmiValue = this.member.weight / (heightInMeters * heightInMeters);
      this.bmi = bmiValue.toFixed(1);
    } else if (this.member.bmi) {
      // Use BMI from member data if available
      this.bmi =
        typeof this.member.bmi === 'number'
          ? this.member.bmi.toFixed(1)
          : this.member.bmi;
    } else {
      this.bmi = '---';
    }
  }

  getBMICategory(): string {
    if (this.bmi === '---') return '';

    const bmiValue = parseFloat(this.bmi);
    if (bmiValue < 18.5) return 'underweight';
    if (bmiValue >= 18.5 && bmiValue < 25) return 'normal';
    if (bmiValue >= 25 && bmiValue < 30) return 'overweight';
    return 'obese';
  }

  getActiveDuration(): string {
    // Use activeTime from API response and calculate total days between start and end
    if (this.member.startTime && this.member.endTime) {
      try {
        // Extract just the date parts to avoid timezone conversion issues
        const startDateString = this.member.startTime.split('T')[0];
        const endDateString = this.member.endTime.split('T')[0];

        const [startYear, startMonth, startDay] = startDateString.split('-');
        const [endYear, endMonth, endDay] = endDateString.split('-');

        const startDate = new Date(
          parseInt(startYear),
          parseInt(startMonth) - 1,
          parseInt(startDay)
        );
        const endDate = new Date(
          parseInt(endYear),
          parseInt(endMonth) - 1,
          parseInt(endDay)
        );

        // Calculate total days between start and end dates (inclusive)
        const timeDifference = endDate.getTime() - startDate.getTime();
        const totalDays = Math.floor(timeDifference / (1000 * 3600 * 24)) + 1; // +1 to include both start and end days

        // Get active days from API response
        const activeDays = this.member.activeTime || 0;

        if (totalDays > 0) {
          return `${activeDays} of ${totalDays} days`;
        }
      } catch (error) {
        console.error('Error calculating duration:', error);
      }
    }
    return '---';
  }

  getFormattedStartDate(): string {
    // Format the startTime from API response
    if (this.member.startTime) {
      try {
        // Extract just the date part to avoid timezone conversion issues
        const dateString = this.member.startTime.split('T')[0];
        const [year, month, day] = dateString.split('-');
        const date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day)
        );

        return formatDate(date, 'MMMM d, y', 'en-US');
      } catch (error) {
        console.error('Error formatting start date:', error);
        return this.member.startTime; // Fallback to raw value
      }
    }
    return '---';
  }

  getFormattedEndDate(): string {
    // Format the endTime from API response
    if (this.member.endTime) {
      try {
        // Extract just the date part to avoid timezone conversion issues
        const dateString = this.member.endTime.split('T')[0];
        const [year, month, day] = dateString.split('-');
        const date = new Date(
          parseInt(year),
          parseInt(month) - 1,
          parseInt(day)
        );

        return formatDate(date, 'MMMM d, y', 'en-US');
      } catch (error) {
        console.error('Error formatting end date:', error);
        return this.member.endTime; // Fallback to raw value
      }
    }
    return '---';
  }

  private isMemberAssigned(): boolean {
    // First check the isFitnessPlanAssigned flag from API
    if (this.member.isFitnessPlanAssigned === true) {
      return true;
    }

    // Check if member has startTime and endTime
    if (!this.member.startTime || !this.member.endTime) {
      // Fallback to legacy logic for backwards compatibility
      return !!(
        this.member.fitnessPlanAssignedDate || this.member.hasExerciseAllotments
      );
    }

    try {
      // Extract just the date parts to avoid timezone conversion issues
      const startDateString = this.member.startTime.split('T')[0];
      const endDateString = this.member.endTime.split('T')[0];

      const [startYear, startMonth, startDay] = startDateString.split('-');
      const [endYear, endMonth, endDay] = endDateString.split('-');

      const startDate = new Date(
        parseInt(startYear),
        parseInt(startMonth) - 1,
        parseInt(startDay)
      );
      const endDate = new Date(
        parseInt(endYear),
        parseInt(endMonth) - 1,
        parseInt(endDay)
      );

      // Get current date and next two days
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day

      const dayAfterTomorrow = new Date(today);
      dayAfterTomorrow.setDate(today.getDate() + 2); // Current day + next 2 days

      // Check if the plan covers the current day and next two days
      // Plan is considered active if it starts on or before day after tomorrow
      // and ends on or after today
      return startDate <= dayAfterTomorrow && endDate >= today;
    } catch (error) {
      console.error('Error checking member assignment status:', error);
      // Fallback to legacy logic on error
      return !!(
        this.member.fitnessPlanAssignedDate || this.member.hasExerciseAllotments
      );
    }
  }

  getFitnessPlanName() {
    // Check for fitness plan for current date only
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const startDate = `${year}-${month}-${day}T00:00:00.000Z`;
    const endDate = `${year}-${month}-${day}T00:00:00.000Z`;
    
    console.log('Today date components:', { year, month, day });
    console.log('Checking fitness plan for member:', this.member.id, 'Date range:', startDate, 'to', endDate);
    
    this.fitnessPlanService.checkIsFitnessPlanOngoing(this.member.id, startDate, endDate).subscribe({
      next: (response: any) => {
        console.log('Fitness plan ongoing check response:', response);
        if (response && response.isExist && response.fitnessPlan && response.fitnessPlan.fitnessPlanName) {
          this.fitnessPlanName = response.fitnessPlan.fitnessPlanName;
        }
      },
      error: (error) => {
        console.error('Error checking fitness plan ongoing status:', error);
      }
    });
  }

  getMemberDataForNavigation() {
    return {
      id: this.member.id,
      firstName: this.member.firstName,
      lastName: this.member.lastName,
      fullName: this.member.fullName,
      age: this.member.age,
      height: this.member.height,
      weight: this.member.weight,
      gender: this.member.gender,
      bmi: this.bmi,
      bmiCategory: this.getBMICategory(),
      profilePicture: this.member.profilePicture,
      hasExistingPlan: this.hasExistingPlan,
      lastAssignedDate: this.getFormattedStartDate(),
    };
  }
}
